import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText,
  ExternalLink,
  Search,
  Filter,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import api from '../../lib/api';
import { formatDate } from '../../utils/dateUtils';
import Pagination from '../Pagination';

interface BudgetDocument {
  id: number;
  item_id: number | null;
  dept_id: number | null;
  doc_type: string;
  doc_title: string;
  doc_url: string;
  published_date: string | null;
  source: string | null;
  is_primary_source: boolean;
  dept_code: string | null;
  dept_name: string | null;
}

interface DocumentFilterOptions {
  docTypes: string[];
  sources: string[];
}

interface Department {
  id: number;
  dept_code: string;
  dept_name: string;
}

interface BudgetDocumentsProps {
  deptId?: number;
  compact?: boolean;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  briefing: 'Briefing',
  hearing: 'Hearing',
  supplemental: 'Supplemental',
  figure_setting: 'Figure Setting',
  figure_setting_comeback: 'Figure Setting Comeback',
  potential_legislation: 'Potential Legislation',
  memo: 'Memo',
  long_bill_narrative: 'Long Bill Narrative',
  narrative: 'Narrative',
  schedule_13: 'Schedule 13',
  budget_letter: 'Budget Letter',
  general_fund_overview: 'General Fund Overview',
  other: 'Other',
};

const DOC_TYPE_COLORS: Record<string, string> = {
  briefing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  hearing: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  supplemental: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  figure_setting: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  figure_setting_comeback: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  potential_legislation: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  memo: 'bg-slate-100 text-slate-800 dark:bg-slate-700/30 dark:text-slate-300',
  long_bill_narrative: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  narrative: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
};


const BudgetDocuments: React.FC<BudgetDocumentsProps> = ({
  deptId,
  compact = false,
}) => {
  const [documents, setDocuments] = useState<BudgetDocument[]>([]);
  const [filterOptions, setFilterOptions] = useState<DocumentFilterOptions>({ docTypes: [], sources: [] });
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDeptId, setSelectedDeptId] = useState<string>(deptId ? String(deptId) : '');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const limit = compact ? 10 : 25;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const effectiveDeptId = deptId ? String(deptId) : selectedDeptId;

  const fetchDocuments = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: Record<string, string> = { page: String(page), limit: String(limit) };
      if (effectiveDeptId) params.dept_id = effectiveDeptId;
      if (docTypeFilter) params.doc_type = docTypeFilter;
      if (debouncedSearch) params.search = debouncedSearch;

      const res = await api.get('/budget/documents', { params });
      if (res.data.success) {
        setDocuments(res.data.data.documents);
        setTotal(res.data.data.total);
      }
    } catch {
      // Silently handle - empty state shown
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, effectiveDeptId, docTypeFilter, debouncedSearch]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    api.get('/budget/documents/filter-options').then(res => {
      if (res.data.success) setFilterOptions(res.data.data);
    }).catch(() => {});

    if (!deptId) {
      api.get('/budget/departments').then(res => {
        if (res.data.success) setDepartments(res.data.data);
      }).catch(() => {});
    }
  }, [deptId]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [docTypeFilter, debouncedSearch, selectedDeptId]);

  const totalPages = useMemo(() => Math.ceil(total / limit), [total, limit]);

  // Group documents by date
  const groupedByDate = useMemo(() => {
    const groups: Record<string, BudgetDocument[]> = {};
    for (const doc of documents) {
      const key = doc.published_date ? formatDate(doc.published_date) : 'Undated';
      if (!groups[key]) groups[key] = [];
      groups[key].push(doc);
    }
    return groups;
  }, [documents]);

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary placeholder-text-tertiary dark:placeholder-text-dark-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
        >
          <Filter className="w-4 h-4" />
          <span className="text-sm">Filters</span>
          {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {showFilters && (
        <div className="flex flex-wrap gap-3 p-3 card">
          <select
            value={docTypeFilter}
            onChange={e => setDocTypeFilter(e.target.value)}
            className="px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Types</option>
            {filterOptions.docTypes.map(t => (
              <option key={t} value={t}>{DOC_TYPE_LABELS[t] || t}</option>
            ))}
          </select>

          {!deptId && departments.length > 0 && (
            <select
              value={selectedDeptId}
              onChange={e => setSelectedDeptId(e.target.value)}
              className="px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Departments</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.dept_name}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
          {total.toLocaleString()} document{total !== 1 ? 's' : ''} found
        </p>
        {isLoading && (
          <Loader2 className="w-4 h-4 animate-spin text-text-secondary dark:text-text-dark-secondary" />
        )}
      </div>

      {/* Documents List */}
      {isLoading && documents.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-text-tertiary dark:text-text-dark-tertiary mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-2">
            No Documents Found
          </h3>
          <p className="text-text-secondary dark:text-text-dark-secondary">
            {debouncedSearch || docTypeFilter
              ? 'No documents match your search criteria.'
              : 'No budget documents have been imported yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByDate).map(([dateLabel, docs]) => (
            <div key={dateLabel}>
              <h4 className="text-sm font-semibold text-text-secondary dark:text-text-dark-secondary mb-2 uppercase tracking-wider">
                {dateLabel}
              </h4>
              <div className="space-y-2">
                {docs.map(doc => (
                  <div key={`${doc.id}-${doc.dept_id}`} className="card p-3 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            DOC_TYPE_COLORS[doc.doc_type] || 'bg-background-tertiary text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary'
                          }`}>
                            {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
                          </span>
                          {doc.dept_name && (
                            <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                              {doc.dept_name}
                            </span>
                          )}
                        </div>
                        <h5 className="text-sm font-medium text-text-primary dark:text-text-dark-primary leading-snug">
                          {doc.doc_title}
                        </h5>
                      </div>
                      {doc.doc_url && (
                        <a
                          href={doc.doc_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination className="pt-4" page={page} pageCount={totalPages} onPageChange={setPage} />
    </div>
  );
};

export default BudgetDocuments;
